import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CardAction, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ListCard } from '@/components/list-card'
import { Memory } from '@/hooks/use-memories'
import { EntityBadge } from './entity-badge'
import { Edit, Trash2, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useMemoryThreadNavigation } from './use-memory-thread-navigation'

interface MemoryCardProps {
  memory: Memory
  onEdit: (memory: Memory) => void
  onDelete: (memoryId: string) => void
  onEntityClick?: (entityId: string, entityName: string) => void
  isDeleting?: boolean
}

function formatMemoryDate(iso: string) {
  const date = new Date(iso)
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function MemoryCard({ memory, onEdit, onDelete, onEntityClick, isDeleting }: MemoryCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const { openThread, isThreadOpen } = useMemoryThreadNavigation(memory.thread_id)

  const stop = (e: React.MouseEvent) => e.stopPropagation()

  const header = (
    <>
      <CardTitle className="text-xs font-normal text-muted-foreground">
        {formatMemoryDate(memory.created_at)}
      </CardTitle>
      <CardAction className="pointer-events-auto">
        <div className="flex shrink-0 items-center gap-0.5" onClick={stop}>
          {memory.thread_id ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconXs"
                    disabled={isThreadOpen}
                    aria-label={isThreadOpen ? 'Thread already open' : 'Open thread'}
                    onClick={openThread}
                  >
                    <MessageSquare className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {isThreadOpen ? 'Thread already open' : 'Open thread'}
              </TooltipContent>
            </Tooltip>
          ) : null}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="iconXs"
                aria-label="Edit memory"
                onClick={(e) => {
                  stop(e)
                  onEdit(memory)
                }}
              >
                <Edit className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="iconXs"
                aria-label="Delete memory"
                className="text-destructive hover:text-destructive"
                disabled={isDeleting}
                onClick={(e) => {
                  stop(e)
                  onDelete(memory.id)
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete</TooltipContent>
          </Tooltip>
        </div>
      </CardAction>
    </>
  )

  const body = (
    <div className="pointer-events-auto flex flex-col gap-2">
      <p className="line-clamp-3 text-sm leading-snug text-foreground">{memory.content}</p>
      {memory.source_message_text ? (
        <blockquote className="line-clamp-2 rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs italic text-muted-foreground">
          {memory.source_message_text}
        </blockquote>
      ) : null}
    </div>
  )

  const footer = (
    <div className="pointer-events-auto space-y-2 border-t border-border/40 pt-2.5">
      <div className="flex flex-wrap gap-1.5">
        <Badge variant="secondary">{memory.memory_type}</Badge>
        <Badge variant="outline">
          {memory.category}
          {memory.subcategory ? ` · ${memory.subcategory}` : ''}
        </Badge>
        <Badge variant="secondary">{memory.importance}</Badge>
      </div>

      {memory.entities && memory.entities.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {memory.entities.map((entity) => (
            <EntityBadge
              key={entity.id}
              entity={entity}
              onClick={() => onEntityClick?.(entity.id, entity.name)}
            />
          ))}
        </div>
      ) : null}

      {memory.related_memories && memory.related_memories.length > 0 ? (
        <div>
          <button
            type="button"
            onClick={(e) => {
              stop(e)
              setIsExpanded(!isExpanded)
            }}
            className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            {isExpanded ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
            <span>{memory.related_memories.length} related memories</span>
          </button>
          {isExpanded ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Memory IDs: {memory.related_memories.join(', ')}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )

  return (
    <ListCard
      header={header}
      body={body}
      footer={footer}
      className={cn(
        'h-full transition-colors',
        'hover:border-foreground/20 hover:bg-muted/30',
      )}
      bodyClassName="pointer-events-none"
      footerClassName="mt-auto border-t-0 px-0"
    />
  )
}
